import { useState } from 'react';
import { Link, router, usePage } from '@inertiajs/react';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button } from '../../../components/ui';
import { Eye, ArrowRight } from 'lucide-react';
import Icon from '../../../components/ui/Icon';
import { cn } from '../../../lib/cn';

const roleBadgeColors = {
  admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  teacher: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  student: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

function MasteryBadge({ label, count, colorClass }) {
  if (count === 0) return null;
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full', colorClass)}>
      <span className="font-bold">{count}</span> {label}
    </span>
  );
}

function ConceptRow({ concept, colorClass }) {
  const pct = Math.round(concept.mastery_level * 100);
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-gray-900 dark:text-white truncate mr-3">{concept.label}</span>
      <div className="flex items-center gap-2 shrink-0">
        <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div className={cn('h-full rounded-full', colorClass)} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400 w-8 text-right">{pct}%</span>
        <span className="text-xs text-gray-400 dark:text-gray-500">({concept.attempts})</span>
      </div>
    </div>
  );
}

function GrammarProfile({ userId, summary }) {
  const { mastery_overview, top_struggles, top_strengths, observation_counts } = summary;

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Grammar Profile</h3>
        <div className="flex items-center gap-3">
          <Link
            href={`/teacher/users/${userId}/grammar`}
            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Manage <ArrowRight className="w-3 h-3" />
          </Link>
          <Link
            href={`/teacher/concepts/grammar?student_id=${userId}`}
            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            View Knowledge Graph <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* Mastery Overview */}
      <div className="flex flex-wrap gap-2 mb-5">
        <MasteryBadge label="Mastered" count={mastery_overview.mastered} colorClass="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" />
        <MasteryBadge label="Developing" count={mastery_overview.developing} colorClass="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" />
        <MasteryBadge label="Weak" count={mastery_overview.weak} colorClass="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" />
        <span className="text-xs text-gray-500 dark:text-gray-400 self-center ml-1">
          {mastery_overview.total} concepts assessed
        </span>
      </div>

      {/* Top Struggles & Strengths side by side */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-5">
        {top_struggles.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wide mb-2">Top Struggles</h4>
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {top_struggles.map(c => (
                <ConceptRow key={c.concept_id} concept={c} colorClass="bg-red-500" />
              ))}
            </div>
          </div>
        )}
        {top_strengths.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wide mb-2">Top Strengths</h4>
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {top_strengths.map(c => (
                <ConceptRow key={c.concept_id} concept={c} colorClass="bg-green-500" />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Observation Activity */}
      <div className="text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800 pt-3">
        {observation_counts.total} observations: {observation_counts.errors} error{observation_counts.errors !== 1 ? 's' : ''}, {observation_counts.correct_usage} correct usage{observation_counts.correct_usage !== 1 ? 's' : ''}
      </div>
    </Card>
  );
}

// Mirrors GrammarProfile but driven by UserWordCard mastery_bucket /
// progress_percent / reps. Buckets: mastered (green) / learning (amber) /
// not_started (gray). Top struggles = lowest-progress onboarded cards;
// top strengths = highest-progress onboarded cards.
function WordRow({ word, colorClass }) {
  const pct = Math.round(word.progress_percent);
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-gray-900 dark:text-white truncate mr-3">{word.lemma}</span>
      <div className="flex items-center gap-2 shrink-0">
        <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div className={cn('h-full rounded-full', colorClass)} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400 w-8 text-right tabular-nums">{pct}%</span>
        <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">({word.reps})</span>
      </div>
    </div>
  );
}

// TODO(teacher-tools): let teachers drill into a student's practice
// sessions from this card — onboarding + review history, per-card attempts
// with Stage 1/3/5 responses and grader feedback, so they can spot systemic
// misconceptions (confused definitions, consistent spelling slips, etc.).
// Likely a new Teacher::VocabularySessionsController + a session-detail page
// linked from the mastery badges and Top Struggles rows.
function VocabularyProfile({ userId, summary }) {
  const { mastery_overview, top_struggles, top_strengths, review_counts } = summary;

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Vocabulary Profile</h3>
        <Link
          href={`/teacher/users/${userId}/vocabulary`}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Manage <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {/* Mastery Overview */}
      <div className="flex flex-wrap gap-2 mb-5">
        <MasteryBadge label="Mastered" count={mastery_overview.mastered} colorClass="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" />
        <MasteryBadge label="Learning" count={mastery_overview.learning} colorClass="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" />
        <MasteryBadge label="Not started" count={mastery_overview.not_started} colorClass="bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400" />
        <span className="text-xs text-gray-500 dark:text-gray-400 self-center ml-1">
          {mastery_overview.total} word{mastery_overview.total !== 1 ? 's' : ''} tracked
        </span>
      </div>

      {/* Top Struggles & Strengths side by side */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-5">
        {top_struggles.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-2">Top Struggles</h4>
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {top_struggles.map(w => (
                <WordRow key={w.card_id} word={w} colorClass="bg-amber-500" />
              ))}
            </div>
          </div>
        )}
        {top_strengths.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wide mb-2">Top Strengths</h4>
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {top_strengths.map(w => (
                <WordRow key={w.card_id} word={w} colorClass="bg-green-500" />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Review Activity */}
      <div className="text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800 pt-3">
        {review_counts.total} review{review_counts.total !== 1 ? 's' : ''}: {review_counts.correct} correct
      </div>
    </Card>
  );
}

const READING_LEVEL_LABELS = {
  elementary: 'Elementary',
  middle: 'Middle',
  high: 'High',
};

function ReadingLevelControl({ userId, override, derivedLevel }) {
  const [value, setValue] = useState(override || '');
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);
    router.patch(
      `/teacher/users/${userId}/reading_level`,
      { reading_level_override: value },
      { onFinish: () => setSaving(false) }
    );
  };

  return (
    <div>
      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Reading level</dt>
      <dd className="mt-1 flex items-center gap-2">
        <select
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="text-sm rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white py-1 px-2"
        >
          <option value="">Auto ({READING_LEVEL_LABELS[derivedLevel] || derivedLevel})</option>
          {Object.entries(READING_LEVEL_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <Button variant="secondary" onClick={handleSave} disabled={saving || value === (override || '')}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </dd>
      <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
        Controls the vocabulary definition level shown to this student. Auto is derived from their VST score / grade.
      </p>
    </div>
  );
}

function EnrolledCourses({ courses }) {
  if (!courses || courses.length === 0) return null;

  return (
    <Card className="p-6">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
        Enrolled courses
      </h3>
      <ul className="divide-y divide-gray-100 dark:divide-gray-800">
        {courses.map((course) => (
          <li key={course.id}>
            <Link
              href={`/teacher/courses/${course.id}`}
              className="group flex items-center justify-between py-2.5 -mx-2 px-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300 truncate">
                  {course.title}
                </p>
                {course.teacher_name && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {course.teacher_name}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                {course.status && course.status !== 'active' && (
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                    {course.status}
                  </span>
                )}
                <ArrowRight className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 group-hover:text-blue-500" strokeWidth={1.5} />
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default function UsersShow({ user, enrolled_courses, grammar_summary, vocabulary_summary, recent_vocab_sessions = [], reading_level_editable = false }) {
  const { auth } = usePage().props;
  const isAdmin = auth?.user?.role === 'admin';

  const memberSince = new Date(user.created_at).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const handleImpersonate = () => {
    router.post(`/admin/users/${user.id}/impersonate`);
  };

  return (
    <TeacherShell scrollTitle={<>{isAdmin && <Link href="/admin/users" className="hover:text-blue-600 dark:hover:text-blue-400">Users</Link>}{isAdmin && '  ›  '}{user.full_name}</>}>
      <TeacherPageHeader
        title={user.full_name}
        subtitle={user.email}
        backLink={isAdmin ? '/admin/users' : undefined}
        backLabel={isAdmin ? 'Back to users' : undefined}
        actions={isAdmin ? (
          <div className="flex items-center gap-3">
            <Link href={`/admin/users/${user.id}/edit`}>
              <Button variant="secondary">Edit</Button>
            </Link>
            <Button variant="ghost" onClick={handleImpersonate}>
              <Icon icon={Eye} size="sm" className="mr-2" />
              Impersonate
            </Button>
          </div>
        ) : null}
      />

      <div className="max-w-2xl space-y-6">
        <Card className="p-6">
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
              <dd className="mt-1 text-gray-900 dark:text-white">{user.full_name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</dt>
              <dd className="mt-1 text-gray-900 dark:text-white">{user.email || 'Not set'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Role</dt>
              <dd className="mt-1">
                <span className={cn('text-xs font-medium px-2 py-0.5 rounded', roleBadgeColors[user.role] || 'bg-gray-100 text-gray-600')}>
                  {user.role}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Organization</dt>
              <dd className="mt-1 text-gray-900 dark:text-white">{user.organization_name || 'None'}</dd>
            </div>
            {user.grade_level && (
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Grade level</dt>
                <dd className="mt-1 text-gray-900 dark:text-white">{user.grade_level}</dd>
              </div>
            )}
            {reading_level_editable && (user.role === 'student' || user.role === 'guest') && (
              <ReadingLevelControl
                userId={user.id}
                override={user.reading_level_override}
                derivedLevel={user.derived_reading_level}
              />
            )}
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Member since</dt>
              <dd className="mt-1 text-gray-900 dark:text-white">{memberSince}</dd>
            </div>
          </dl>
        </Card>

        <EnrolledCourses courses={enrolled_courses} />

        {grammar_summary && (
          <GrammarProfile userId={user.id} summary={grammar_summary} />
        )}

        {vocabulary_summary && (
          <VocabularyProfile userId={user.id} summary={vocabulary_summary} />
        )}

        {recent_vocab_sessions.length > 0 && (
          <RecentVocabSessions userId={user.id} sessions={recent_vocab_sessions} />
        )}
      </div>
    </TeacherShell>
  );
}

function RecentVocabSessions({ userId, sessions }) {
  return (
    <Card className="p-6">
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wide">
        Recent vocab sessions
      </h3>
      <ul className="space-y-1">
        {sessions.map((s) => {
          const when = s.completed_at || s.updated_at;
          const whenLabel = when ? new Date(when).toLocaleString() : '—';
          const typeLabel = s.type === 'review' ? 'Practice' : 'Onboarding';
          const subdetail =
            s.type === 'review' && s.answered != null && s.total != null
              ? `${s.answered}/${s.total} answered`
              : `${s.word_count} word${s.word_count === 1 ? '' : 's'}`;
          return (
            <li key={s.id}>
              <Link
                href={`/teacher/users/${userId}/vocabulary_session_replays/${s.id}`}
                className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
              >
                <div className="min-w-0 flex-1 flex items-baseline gap-3">
                  <span className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 w-20 flex-shrink-0">
                    {typeLabel}
                  </span>
                  <span className="text-sm text-gray-700 dark:text-gray-300 flex-shrink-0">
                    {subdetail}
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
                    {whenLabel}
                  </span>
                </div>
                <span className={cn(
                  'text-xs px-2 py-0.5 rounded-full flex-shrink-0',
                  s.status === 'completed'   ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' :
                  s.status === 'in_progress' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
                  s.status === 'abandoned'   ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' :
                  'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                )}>
                  {s.status.replace('_', ' ')}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
